import { useGetTimetableStructureQuery } from '@schooly/api';
import { Loading, ModalLarge, NotFoundContent } from '@schooly/style';
import { FC, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useRouter } from '../../../../context/router/useRouter';
import { WithTimetable } from '../../../../context/timetable/WithTimetable';
import useSchoolYears from '../../../../hooks/useSchoolYears';
import { SCHOOL_YEAR_ID_PARAM, STRUCTURE_ID_PARAM } from '../utils';
import { TimetableModalContent } from './TimetableModalContent';
import { CreateTimetableSelectModal } from './CreateTimetableSelectModal';

export const CreateTimetableModal: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const schoolYearId = searchParams.get(SCHOOL_YEAR_ID_PARAM);
  const structureId = searchParams.get(STRUCTURE_ID_PARAM);
  const { goBack } = useRouter();

  const {
    schoolYears,
    getSchoolYearById,
    isPreviousSchoolYear,
    isLoading: isLoadingSchoolYears,
  } = useSchoolYears();

  const { data: structure, isFetching: isFetchingStructure } = useGetTimetableStructureQuery(
    { structureId: structureId ?? '' },
    {
      enabled: !!structureId,
      refetchOnMount: 'always',
    },
  );

  const schoolYear = useMemo(
    () => (schoolYearId ? getSchoolYearById(schoolYearId) : undefined),
    [schoolYearId, getSchoolYearById],
  );

  const handleClose = useCallback(() => navigate('/settings/timetables'), [navigate]);

  const hasValidParams = schoolYearId && structureId;

  if (!hasValidParams) {
    return (
      <CreateTimetableSelectModal
        schoolYears={schoolYears.filter((y) => !isPreviousSchoolYear(y))}
        isLoadingSchoolYears={isLoadingSchoolYears}
        onClose={handleClose}
        onCreate={(yearId, structId) => {
          setSearchParams({ [SCHOOL_YEAR_ID_PARAM]: yearId, [STRUCTURE_ID_PARAM]: structId });
        }}
        onImport={(yearId, structId) => {
          navigate(
            `/settings/timetables/import?${SCHOOL_YEAR_ID_PARAM}=${yearId}&${STRUCTURE_ID_PARAM}=${structId}`,
          );
        }}
      />
    );
  }

  const isLoading = isLoadingSchoolYears || isFetchingStructure;

  if (isLoading) {
    return (
      <ModalLarge open onClose={handleClose}>
        <Loading />
      </ModalLarge>
    );
  }

  const notFound = !schoolYear || !structure || isPreviousSchoolYear(schoolYear);

  if (notFound) {
    return (
      <ModalLarge open onClose={handleClose}>
        <NotFoundContent goBack={goBack} />
      </ModalLarge>
    );
  }

  return (
    <WithTimetable structure={structure} schoolYear={schoolYear}>
      <TimetableModalContent onClose={handleClose} mode="edit" />
    </WithTimetable>
  );
};
